// Suchen in Tabs (D-071): Jede Suche ist ein Tab mit Frage, Treffern und geöffnetem Thread. Die Tabs
// überleben den Wechsel in den Posteingang und das Neuladen (Fragen in localStorage, Treffer werden dann
// neu geholt – Bob-Antworten nicht, die kosten Geld und laufen erst wieder auf Klick).
import { useSyncExternalStore } from 'react';
import type { SearchResponse } from './api.ts';

export type SuchModus = 'suchen' | 'bob';
export type SuchReiter = 'alles' | 'mails' | 'anhaenge' | 'einrichtungen';
export type BobStand = {
  laden: boolean;
  antwort?: string;
  schritte?: Array<{ tool: string; input: unknown }>;
  frage?: string;
  kosten?: number;
  error?: string;
};
export type SuchTab = {
  id: number;
  q: string; // zuletzt ausgeführte Suche bzw. Frage
  eingabe: string; // Text im Suchfeld (kann von q abweichen, bis Enter gedrückt wird)
  modus: SuchModus;
  res: SearchResponse | null;
  laden: boolean;
  bob: BobStand | null;
  reiter: SuchReiter;
  thread: number | null; // in diesem Tab geöffneter Thread
};
type Stand = { tabs: SuchTab[]; aktiv: number };
type Gemerkt = { tabs: Array<Pick<SuchTab, 'id' | 'q' | 'modus' | 'thread'>>; aktiv: number };

const KEY = 'mm.suche';
let naechsteId = 1;
const leerTab = (teil: Partial<SuchTab> = {}): SuchTab => ({
  id: naechsteId++,
  q: '',
  eingabe: '',
  modus: 'suchen',
  res: null,
  laden: false,
  bob: null,
  reiter: 'alles',
  thread: null,
  ...teil,
});

function laden(): Stand {
  try {
    const g = JSON.parse(localStorage.getItem(KEY) ?? '') as Gemerkt;
    if (!Array.isArray(g.tabs) || !g.tabs.length) throw new Error('leer');
    const tabs = g.tabs.map((t) => leerTab({ ...t, eingabe: t.q }));
    naechsteId = Math.max(...tabs.map((t) => t.id)) + 1;
    return { tabs, aktiv: tabs.some((t) => t.id === g.aktiv) ? g.aktiv : tabs[0]!.id };
  } catch {
    const t = leerTab();
    return { tabs: [t], aktiv: t.id };
  }
}
let stand: Stand = laden();
const hoerer = new Set<() => void>();
function setze(neu: Stand) {
  stand = neu;
  try {
    const g: Gemerkt = {
      tabs: neu.tabs.map(({ id, q, modus, thread }) => ({ id, q, modus, thread })),
      aktiv: neu.aktiv,
    };
    localStorage.setItem(KEY, JSON.stringify(g));
  } catch {
    /* privates Fenster o. ä. */
  }
  hoerer.forEach((h) => h());
}

export function useSuche() {
  return useSyncExternalStore(
    (h) => (hoerer.add(h), () => hoerer.delete(h)),
    () => stand,
  );
}
export const getSuche = () => stand;
export const aktiverTab = () => stand.tabs.find((t) => t.id === stand.aktiv) ?? stand.tabs[0]!;

export function aendereTab(
  id: number,
  patch: Partial<SuchTab> | ((t: SuchTab) => Partial<SuchTab>),
) {
  setze({
    ...stand,
    tabs: stand.tabs.map((t) =>
      t.id === id ? { ...t, ...(typeof patch === 'function' ? patch(t) : patch) } : t,
    ),
  });
}
export function waehleTab(id: number) {
  if (stand.tabs.some((t) => t.id === id)) setze({ ...stand, aktiv: id });
}
export function neuerTab(teil: Partial<SuchTab> = {}): SuchTab {
  const t = leerTab(teil);
  setze({ tabs: [...stand.tabs, t], aktiv: t.id });
  return t;
}
/** Tab für eine Frage von außen (?q=…): vorhandener Tab mit derselben Frage, sonst der leere aktive, sonst neu. */
export function tabFuer(q: string, modus: SuchModus): SuchTab {
  const da = stand.tabs.find((t) => t.q === q && t.modus === modus);
  if (da) {
    waehleTab(da.id);
    return da;
  }
  const a = aktiverTab();
  if (!a.q && !a.eingabe) {
    aendereTab(a.id, { q, eingabe: q, modus, res: null, bob: null });
    return { ...a, q, eingabe: q, modus };
  }
  return neuerTab({ q, eingabe: q, modus });
}
/** Schließt einen Tab; der letzte wird nur geleert. Gibt den danach aktiven Tab zurück. */
export function schliesseTab(id: number): SuchTab {
  const i = stand.tabs.findIndex((t) => t.id === id);
  if (i < 0) return aktiverTab();
  if (stand.tabs.length === 1) {
    const t = leerTab();
    setze({ tabs: [t], aktiv: t.id });
    return t;
  }
  const rest = stand.tabs.filter((t) => t.id !== id);
  const aktiv = stand.aktiv === id ? (rest[i] ?? rest[i - 1] ?? rest[0])!.id : stand.aktiv;
  setze({ tabs: rest, aktiv });
  return rest.find((t) => t.id === aktiv)!;
}
/** Pfad zur Suchseite: bringt den im aktiven Tab geöffneten Thread gleich wieder mit. */
export const suchPfad = () => {
  const t = aktiverTab();
  return t.thread ? `/search/t/${t.thread}` : '/search';
};
/** Nächster Treffer im aktiven Tab nach einem Thread (für „Senden & erledigen“ und j/k aus der Suche heraus). */
export function naechsterTreffer(id: number): number | null {
  const l = aktiverTab().res?.threads ?? [];
  const i = l.findIndex((t) => t.id === id);
  if (i < 0) return l[0]?.id ?? null;
  return (l[i + 1] ?? l[i - 1])?.id ?? null;
}
